import { useTranslation } from 'react-i18next'
import { t as translate } from '../lib/i18n'
import { useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Collapse, Group, Loader, Pill, Skeleton, Stack, Text, TextInput, UnstyledButton } from '@mantine/core'
import { IconBrandTeams, IconChevronRight, IconLink, IconSearch } from '@tabler/icons-react'
import { planIdFromInput } from '../lib/plannerUrl'

async function get(url) {
  let response
  try { response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }) }
  catch {
    const error = new Error(translate("Microsoft kon niet worden bereikt. Probeer opnieuw."))
    error.retryable = true
    throw error
  }
  if (response.redirected) throw new Error(translate("Meld opnieuw aan met Microsoft."))
  const result = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(result.error || translate("Ophalen is niet gelukt."))
    error.retryable = result.retryable === true
    throw error
  }
  return result
}

function TeamBoards({ team, query, isSelected, toggle }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [plans, setPlans] = useState(null)
  const [error, setError] = useState(null)
  function load() {
    setError(null)
    get(`/teams/catalog?team_id=${encodeURIComponent(team.id)}`).then(result => setPlans(result.plans)).catch(setError)
  }
  function expand() {
    setOpen(!open)
    if (plans || open || error) return
    load()
  }
  const visible = (plans || []).filter(plan => plan.title.toLowerCase().includes(query))
  return <div className="board-picker-team">
    <UnstyledButton className="board-picker-team-toggle" onClick={expand} aria-expanded={open}><IconChevronRight size={14} className={open ? 'is-open' : undefined}/><IconBrandTeams size={15}/><span>{team.displayName}</span></UnstyledButton>
    <Collapse expanded={open}><div className="board-picker-team-plans">
      {error ? <Stack gap={4}><Text size="xs" c="red">{t(error.message)}</Text>{error.retryable && <Button size="xs" variant="subtle" onClick={load}>{t("Opnieuw proberen")}</Button>}</Stack> : !plans ? <Group gap={8} py={6}><Loader size={12}/><Text size="xs" c="dimmed">{t("Borden ophalen…")}</Text></Group> : !visible.length ? <Text size="xs" c="dimmed" py={6}>{t("Geen borden gevonden in dit team.")}</Text> : visible.map(plan => <Checkbox key={plan.id} className="board-picker-option" label={plan.title} checked={isSelected(plan.id)} onChange={event => toggle(plan, event.currentTarget.checked)}/>)}
    </div></Collapse>
  </div>
}

export default function BoardPicker({ connected, teamsConnected, preview, csrf, selected, setSelected }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [mine, setMine] = useState(null)
  const [teams, setTeams] = useState(null)
  const [error, setError] = useState('')
  const [link, setLink] = useState('')
  const [linkError, setLinkError] = useState('')
  const [adding, setAdding] = useState(false)
  const readable = connected || preview
  useEffect(() => {
    if (!readable) return
    get('/planner/discover').then(result => setMine(result.plans)).catch(e => { setMine([]); setError(e.message) })
    if (teamsConnected) get('/teams/catalog').then(result => setTeams(result.teams)).catch(e => { setTeams([]); setError(e.message) })
  }, [])
  const isSelected = id => selected.some(board => board.id === id)
  const toggle = (plan, checked) => setSelected(checked ? [...selected.filter(board => board.id !== plan.id), { id: plan.id, title: plan.title }] : selected.filter(board => board.id !== plan.id))
  async function addLink(event) {
    event.preventDefault()
    const id = planIdFromInput(link)
    if (!id) return setLinkError(t("Dit is geen link naar een Planner-bord. Open het bord in Planner en kopieer de link uit de adresbalk."))
    setAdding(true); setLinkError('')
    try { toggle(await get(`/planner/plan?id=${encodeURIComponent(id)}`), true); setLink('') } catch (e) { setLinkError(t(e.message)) } finally { setAdding(false) }
  }
  const search = query.trim().toLowerCase()
  const mineVisible = (mine || []).filter(plan => plan.title.toLowerCase().includes(search))
  if (!readable) return <Alert color="gray">{t("Meld aan met Microsoft om je Planner-borden te kiezen.")}</Alert>
  return <Stack gap="sm" className="board-picker">
    {selected.length > 0 && <Pill.Group>{selected.map(board => <Pill key={board.id} withRemoveButton onRemove={() => toggle(board, false)} removeButtonProps={{ 'aria-label': t("{{value0}} verwijderen", { value0: board.title }) }}>{board.title}</Pill>)}</Pill.Group>}
    <TextInput placeholder={t("Zoek een bord")} aria-label={t("Zoek een bord")} leftSection={<IconSearch size={14}/>} value={query} onChange={event => setQuery(event.target.value)}/>
    <div className="board-picker-list">
      <Text className="board-picker-heading">{t("Borden met jouw taken")}</Text>
      {!mine ? <Stack gap={6} p={6}>{[0, 1, 2].map(key => <Skeleton key={key} height={18} radius={4}/>)}</Stack> : !mineVisible.length ? <Text size="xs" c="dimmed" px={8} py={6}>{search ? t("Geen borden gevonden.") : t("Er zijn nog geen taken aan jou toegewezen.")}</Text> : mineVisible.map(plan => <Checkbox key={plan.id} className="board-picker-option" label={plan.title} checked={isSelected(plan.id)} onChange={event => toggle(plan, event.currentTarget.checked)}/>)}
      {!preview && <>
        <Text className="board-picker-heading">{t("Borden van je teams")}</Text>
        {!teamsConnected ? <div className="board-picker-connect"><Text size="xs" c="dimmed">{t("Microsoft vraagt eenmalig toestemming om je teams te tonen. Je ziet alleen borden waar je zelf toegang toe hebt.")}</Text><form action="/auth/microsoft" method="post"><input type="hidden" name="authenticity_token" value={csrf}/><input type="hidden" name="teams" value="1"/><Button type="submit" size="xs" variant="default" leftSection={<IconBrandTeams size={14}/>}>{t("Teams tonen")}</Button></form></div>
          : !teams ? <Stack gap={6} p={6}>{[0, 1].map(key => <Skeleton key={key} height={18} radius={4}/>)}</Stack>
          : !teams.length ? <Text size="xs" c="dimmed" px={8} py={6}>{t("Je bent geen lid van een team.")}</Text>
          : teams.map(team => <TeamBoards key={team.id} team={team} query={search} isSelected={isSelected} toggle={toggle}/>)}
      </>}
    </div>
    {error && <Text size="xs" c="red">{t(error)}</Text>}
    <form onSubmit={addLink}><Group gap={8} align="flex-start" wrap="nowrap">
      <TextInput className="board-picker-link" placeholder={t("Bord niet in de lijst? Plak de link uit Planner")} aria-label={t("Link naar een Planner-bord")} leftSection={<IconLink size={14}/>} value={link} onChange={event => { setLink(event.target.value); setLinkError('') }} error={linkError || undefined}/>
      <Button type="submit" variant="default" loading={adding} disabled={!link.trim()}>{t("Toevoegen")}</Button>
    </Group></form>
  </Stack>
}
